import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus, ChevronDown, ChevronUp, FileSignature, History, AlertTriangle, Sparkles, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { useChangeOrders, type CreateChangeOrderInput, type SowPricingInfo } from '../hooks/useChangeOrders';
import { FixedBidQuoteLines, MarkupPercentField, type MarkupInput } from './FixedBidMarkup';
import { formatDollars } from '../utils/fixedBidMarkupText';
import { fixedBidQuote, parseMarkupPercent } from '../utils/fixedBidMarkup';
import { parseChangeOrderForConfigItems } from '../utils/changeOrderParser';
import {
  CO_STATUS_LABELS,
  CO_TRANSITION_LABELS,
  allowedTransitions,
  canEditContent,
  canToggleSuggestions,
  coStatusClasses,
  isPrimaryTransition,
  type CoStatus,
} from '../utils/changeOrderLifecycle';
import type { ApplyPlan, ChangeOrderRecord, CoApprovalMethod, SuggestedConfigItemLike, TransitionRequest } from '../types/changeOrder';
import { cellDeepLink, listLeftovers, type LeftoverBlock } from '../utils/discoveryGridApi';
import { useInRouterContext, useNavigate } from 'react-router';

interface ChangeOrderLifecyclePanelProps {
  engagementId: string;
  accessToken: string;
  /** Hide the built-in header (title + summary + new button) */
  hideHeader?: boolean;
  /** Extra controls rendered next to the New button (e.g. "Draft document") */
  extraActions?: ReactNode;
  /** Called after any change so parents can refresh their own view */
  onChanged?: () => void;
  className?: string;
}

type DialogState =
  | { kind: 'new' }
  | { kind: 'edit'; co: ChangeOrderRecord }
  | { kind: 'transition'; co: ChangeOrderRecord; to: CoStatus }
  | null;

function fmtDate(v?: string): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
}

function fmtMoney(amount?: number, currency = 'USD'): string {
  if (typeof amount !== 'number') return '';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

const APPROVAL_METHODS: Array<{ value: CoApprovalMethod; label: string }> = [
  { value: 'signed_document', label: 'Signed document' },
  { value: 'email', label: 'Email confirmation' },
  { value: 'portal', label: 'Accepted in client portal' },
  { value: 'verbal', label: 'Verbal (meeting)' },
];

/**
 * Change orders for one engagement with the PRD §4.4.1 lifecycle:
 * draft → pending approval → approved → applied → billed, plus rejection,
 * cancellation and rework. Clients see it read-only (server decides canWrite).
 */
export function ChangeOrderLifecyclePanel({ engagementId, accessToken, hideHeader = false, extraActions, onChanged, className = '' }: ChangeOrderLifecyclePanelProps) {
  const api = useChangeOrders({ engagementId, accessToken });
  const [dialog, setDialog] = useState<DialogState>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const afterChange = () => {
    setDialog(null);
    onChanged?.();
  };

  const toggleSuggestion = async (co: ChangeOrderRecord, suggestionId: string, accepted: boolean) => {
    const next = (co.suggestedConfigItems ?? []).map((s) => (s.id === suggestionId ? { ...s, accepted } : s));
    const result = await api.update(co.id, { suggestedConfigItems: next });
    if (!result) toast.error(api.error ?? 'Could not update suggestion');
  };

  return (
    <div className={className} data-testid="change-order-panel">
      {!hideHeader && (
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div>
            <h4 className="text-base font-semibold flex items-center gap-2">
              <FileSignature className="size-4 text-slate-500" aria-hidden="true" />
              Change Orders
            </h4>
            {api.summary && api.summary.total > 0 && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {api.summary.total} total · {api.summary.open} open
                {api.summary.hasPendingApproval ? ' · awaiting client approval' : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {extraActions}
            {api.canWrite && (
              <Button size="sm" onClick={() => setDialog({ kind: 'new' })} data-testid="co-new">
                <Plus className="size-4 mr-1" />
                New change order
              </Button>
            )}
          </div>
        </div>
      )}

      {api.error && (
        <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-3" role="alert">
          <AlertTriangle className="size-3.5" aria-hidden="true" />
          {api.error}
        </div>
      )}

      {api.loading && api.changeOrders.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center border rounded-lg">Loading change orders…</p>
      ) : api.changeOrders.length === 0 ? (
        <div className="text-center py-5 border rounded-lg bg-gray-50">
          <p className="text-sm text-muted-foreground">No change orders on this engagement.</p>
          <p className="text-xs text-muted-foreground mt-1">Scope changes after kickoff become change orders; the baseline SOW stays as signed.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {api.changeOrders.map((co) => {
            const open = expanded.has(co.id);
            const transitions = api.canWrite ? allowedTransitions(co.status) : [];
            const suggestions = co.suggestedConfigItems ?? [];
            return (
              <li key={co.id} className="border rounded-lg bg-white" data-testid={`co-${co.changeOrderId}`}>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{co.changeOrderId}</span>
                        <Badge variant="outline" className={`text-[11px] ${coStatusClasses(co.status)}`}>{CO_STATUS_LABELS[co.status]}</Badge>
                        <span className="text-xs text-muted-foreground">added {fmtDate(co.dateAdded)}</span>
                      </div>
                      <p className="text-sm font-medium mt-1">{co.description}</p>
                      <p className={`text-xs text-muted-foreground mt-0.5 ${open ? '' : 'line-clamp-2'}`}>{co.scopeChanges}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 px-2 shrink-0" onClick={() => toggleExpanded(co.id)} aria-expanded={open} aria-label={open ? 'Collapse' : 'Expand'}>
                      {open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                    </Button>
                  </div>

                  {co.pricing && <PriceLine pricing={co.pricing} />}
                  <StatusLine co={co} />

                  {(transitions.length > 0 || (api.canWrite && canEditContent(co.status))) && (
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      {api.canWrite && canEditContent(co.status) && (
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => setDialog({ kind: 'edit', co })}>
                          <Pencil className="size-3.5 mr-1" />
                          Edit
                        </Button>
                      )}
                      {transitions.map((to) => (
                        <Button
                          key={to}
                          size="sm"
                          variant={isPrimaryTransition(to) ? 'default' : 'outline'}
                          className="h-7"
                          disabled={api.busy}
                          onClick={() => setDialog({ kind: 'transition', co, to })}
                          data-testid={`co-${co.changeOrderId}-${to}`}
                        >
                          {CO_TRANSITION_LABELS[to]}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>

                {open && (
                  <div className="border-t px-3 py-3 space-y-3 bg-gray-50/60">
                    {co.notes && (
                      <p className="text-xs text-slate-700">
                        <span className="text-muted-foreground">Notes: </span>
                        {co.notes}
                      </p>
                    )}

                    {suggestions.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-700 flex items-center gap-1 mb-1">
                          <Sparkles className="size-3.5 text-indigo-500" aria-hidden="true" />
                          Suggested config items ({suggestions.filter((s) => s.accepted).length}/{suggestions.length} accepted)
                        </p>
                        <ul className="space-y-1">
                          {suggestions.map((s) => (
                            <li key={s.id} className="flex items-start gap-2 text-xs">
                              <Checkbox
                                id={`sug-${co.id}-${s.id}`}
                                checked={s.accepted}
                                disabled={!api.canWrite || !canToggleSuggestions(co.status) || api.busy}
                                onCheckedChange={(v) => toggleSuggestion(co, s.id, v === true)}
                                className="mt-0.5"
                              />
                              <label htmlFor={`sug-${co.id}-${s.id}`} className="cursor-pointer">
                                <span className="font-medium">{s.name}</span>
                                <span className="text-muted-foreground"> · {s.category}</span>
                                {s.description && <span className="block text-muted-foreground">{s.description}</span>}
                              </label>
                            </li>
                          ))}
                        </ul>
                        {co.applied && (
                          <p className="text-xs text-blue-700 mt-1">
                            Applied {fmtDate(co.applied.at)}: {co.applied.createdConfigItemIds.length} config item(s) created
                            {co.applied.skippedSuggestionIds.length ? `, ${co.applied.skippedSuggestionIds.length} skipped` : ''}.
                          </p>
                        )}
                      </div>
                    )}

                    <CarriedCellsSummary co={co} companyId={api.companyId} />

                    {(co.linkedScopeDeltas?.length ?? 0) > 0 && (
                      <p className="text-xs text-slate-700">
                        Linked to {co.linkedScopeDeltas!.length} scope delta{co.linkedScopeDeltas!.length === 1 ? '' : 's'} from discovery.
                      </p>
                    )}

                    {(co.history?.length ?? 0) > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-700 flex items-center gap-1 mb-1">
                          <History className="size-3.5" aria-hidden="true" />
                          History
                        </p>
                        <ol className="space-y-0.5">
                          {[...co.history!].reverse().map((h, i) => (
                            <li key={i} className="text-xs text-muted-foreground">
                              {fmtDate(h.at)} · {h.from ? `${CO_STATUS_LABELS[h.from]} → ` : ''}
                              {CO_STATUS_LABELS[h.to]}
                              {h.byName ? ` by ${h.byName}` : ''}
                              {h.note ? ` — ${h.note}` : ''}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {dialog?.kind === 'new' && (
        <ChangeOrderFormDialog
          open
          sowPricing={api.sowPricing}
          onClose={() => setDialog(null)}
          busy={api.busy}
          onSubmit={async (input) => {
            const created = await api.create(input);
            if (created) {
              toast.success(`${created.changeOrderId} drafted`);
              afterChange();
            } else toast.error(api.error ?? 'Could not create change order');
          }}
        />
      )}
      {dialog?.kind === 'edit' && (
        <ChangeOrderFormDialog
          open
          existing={dialog.co}
          sowPricing={api.sowPricing}
          onClose={() => setDialog(null)}
          busy={api.busy}
          onSubmit={async (input) => {
            const updated = await api.update(dialog.co.id, input);
            if (updated) {
              toast.success(`${updated.changeOrderId} updated`);
              afterChange();
            } else toast.error(api.error ?? 'Could not update change order');
          }}
        />
      )}
      {dialog?.kind === 'transition' && (
        <TransitionDialog
          co={dialog.co}
          to={dialog.to}
          anotherPending={!!api.summary?.hasPendingApproval && dialog.co.status !== 'pending_approval'}
          busy={api.busy}
          companyId={api.companyId ?? null}
          engagementId={engagementId}
          accessToken={accessToken}
          loadApplyPlan={(acceptIds) => api.applyPlan(dialog.co.id, acceptIds)}
          onClose={() => setDialog(null)}
          onSubmit={async (request) => {
            const result = await api.transition(dialog.co.id, request);
            if (result) {
              const extra = result.applyResult ? ` · ${result.applyResult.createdConfigItemIds.length} config item(s) created` : '';
              const restored = result.carried?.carried ? ` · ${result.carried.carried} discovery cell(s) restored` : '';
              toast.success(`${result.changeOrder.changeOrderId}: ${CO_STATUS_LABELS[result.changeOrder.status]}${extra}${restored}`);
              afterChange();
            } else toast.error(api.error ?? 'Transition failed');
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Discovery cells restored by this change order (spec §4.10, decision 16)
// ---------------------------------------------------------------------------

/** `scopeMap` entries that restored a cell on apply. */
export function carriedCellsOf(co: Pick<ChangeOrderRecord, 'scopeMap'>): Array<{ cellId: string; text?: string; at: string }> {
  return (co.scopeMap ?? []).filter((e) => e.outcome === 'carry' && !!e.cellId).map((e) => ({ cellId: e.cellId!, text: e.text, at: e.at }));
}

/** Links each restored cell to its place on the discovery grid; plain text until the company id is known. */
export function CarriedCellsSummary({ co, companyId }: { co: Pick<ChangeOrderRecord, 'scopeMap' | 'changeOrderId'>; companyId: string | null }) {
  const cells = carriedCellsOf(co);
  if (cells.length === 0) return null;
  return (
    <div data-testid="co-carried-cells">
      <p className="text-xs font-medium text-slate-700 mb-1">
        {cells.length} discovery cell{cells.length === 1 ? '' : 's'} restored into scope by {co.changeOrderId}
      </p>
      <ul className="space-y-0.5">
        {cells.map((c) => (
          <li key={c.cellId} className="text-xs">
            {companyId ? (
              <CellLink href={cellDeepLink(companyId, c.cellId)}>{c.text ?? c.cellId}</CellLink>
            ) : (
              <span className="text-slate-700">{c.text ?? c.cellId}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** An in-app link when a router is mounted (the SOW views are), a plain anchor otherwise. */
function CellLink({ href, children }: { href: string; children: ReactNode }) {
  const inRouter = useInRouterContext();
  const className = 'text-blue-700 hover:underline';
  if (!inRouter) return <a href={href} className={className}>{children}</a>;
  return <RouterCellLink href={href} className={className}>{children}</RouterCellLink>;
}

function RouterCellLink({ href, className, children }: { href: string; className: string; children: ReactNode }) {
  const navigate = useNavigate();
  return (
    <a href={href} className={className} onClick={(e) => { e.preventDefault(); navigate(href); }}>
      {children}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Status line (who / when for the current state)
// ---------------------------------------------------------------------------

function StatusLine({ co }: { co: ChangeOrderRecord }) {
  let text: string | null = null;
  switch (co.status) {
    case 'pending_approval':
      text = co.submittedAt ? `Sent for approval ${fmtDate(co.submittedAt)}; the firm records the client's decision.` : null;
      break;
    case 'approved':
      text = co.approval ? `Approved by ${co.approval.approverName} on ${fmtDate(co.approval.approvedOn)} (${APPROVAL_METHODS.find((m) => m.value === co.approval!.method)?.label ?? co.approval.method}${co.approval.reference ? `, ${co.approval.reference}` : ''}). Not yet applied to scope.` : null;
      break;
    case 'applied':
      text = co.applied ? `Applied ${fmtDate(co.applied.at)}${co.applied.byName ? ` by ${co.applied.byName}` : ''}: ${co.applied.createdConfigItemIds.length} config item(s) added to the engagement.` : null;
      break;
    case 'billed':
      text = co.billing ? `Billed ${fmtDate(co.billing.invoicedAt)}${co.billing.invoiceRef ? ` · ${co.billing.invoiceRef}` : ''}${typeof co.billing.amount === 'number' ? ` · ${fmtMoney(co.billing.amount, co.billing.currency)}` : ''}.` : null;
      break;
    case 'rejected':
      text = co.rejection ? `Rejected ${fmtDate(co.rejection.recordedAt)}: ${co.rejection.reason}` : null;
      break;
    case 'cancelled':
      text = co.cancellation ? `Cancelled ${fmtDate(co.cancellation.at)}${co.cancellation.reason ? `: ${co.cancellation.reason}` : ''}` : null;
      break;
    default:
      text = null;
  }
  if (!text) return null;
  return <p className="text-xs text-slate-600 mt-2">{text}</p>;
}

// ---------------------------------------------------------------------------
// New / edit dialog
// ---------------------------------------------------------------------------

/**
 * A change order's price as the card shows it (clients read it too, so the markup reads as a
 * contingency): the total, and the base plus markup when there is one.
 */
export function PriceLine({ pricing }: { pricing: NonNullable<ChangeOrderRecord['pricing']> }) {
  return (
    <p className="text-xs text-slate-700 mt-1" data-testid="co-price">
      Price {formatDollars(pricing.total)}
      {pricing.markupPercent > 0 && (
        <span className="text-muted-foreground">
          {` · base ${formatDollars(pricing.basePrice)} + ${pricing.markupPercent}% contingency (${formatDollars(pricing.markupAmount)})${pricing.markupSource === 'sow' ? ', as on the SOW' : ''}`}
        </span>
      )}
    </p>
  );
}

export function ChangeOrderFormDialog({ open, existing, sowPricing, busy, onClose, onSubmit }: { open: boolean; existing?: ChangeOrderRecord; sowPricing?: SowPricingInfo | null; busy: boolean; onClose: () => void; onSubmit: (input: CreateChangeOrderInput) => Promise<void> }) {
  const [description, setDescription] = useState(existing?.description ?? '');
  const [scopeChanges, setScopeChanges] = useState(existing?.scopeChanges ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [suggestions, setSuggestions] = useState<SuggestedConfigItemLike[]>(existing?.suggestedConfigItems ?? []);
  // The price (decision 128): on a fixed-bid SOW a change order is a fixed amount plus markup,
  // the SOW's markup unless this change order sets its own. Empty price, no price.
  const fixedBid = !!sowPricing?.fixedBid;
  const sowMarkup = sowPricing?.markupPercent ?? 0;
  const showPrice = fixedBid || !!existing?.pricing;
  const [price, setPrice] = useState<number | ''>(existing?.pricing ? existing.pricing.basePrice : '');
  const [useSowMarkup, setUseSowMarkup] = useState(existing?.pricing?.markupSource !== 'override');
  const [ownMarkup, setOwnMarkup] = useState<MarkupInput>(existing?.pricing?.markupSource === 'override' ? existing.pricing.markupPercent : sowMarkup);
  const ownMarkupCheck = parseMarkupPercent(ownMarkup);
  const markupInUse = !fixedBid ? 0 : useSowMarkup ? sowMarkup : ownMarkup;
  const quote = fixedBidQuote(price, markupInUse);
  const pricing: CreateChangeOrderInput['pricing'] | undefined = price === ''
    ? (existing?.pricing ? null : undefined)
    : { basePrice: Number(price), markupPercent: fixedBid && !useSowMarkup && ownMarkupCheck.ok ? ownMarkupCheck.percent : null };

  const suggest = () => {
    const { suggestedItems } = parseChangeOrderForConfigItems(scopeChanges, description);
    const known = new Set(suggestions.map((s) => s.name));
    const fresh = (suggestedItems as any[]).filter((s) => !known.has(s.name)).map((s) => ({ ...s, accepted: true }));
    setSuggestions([...suggestions, ...fresh]);
    toast.message(fresh.length ? `${fresh.length} config item(s) suggested` : 'No new suggestions found in this text');
  };

  const valid = description.trim().length > 0 && scopeChanges.trim().length > 0;
  const priceValid = useSowMarkup || !fixedBid || ownMarkupCheck.ok;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.changeOrderId}` : 'New change order'}</DialogTitle>
          <DialogDescription>A change order is an additive overlay on the signed SOW. The baseline scope is never edited.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="co-description">Description *</Label>
            <Input id="co-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Add HR service desk" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="co-scope">Scope changes *</Label>
            <Textarea id="co-scope" rows={5} value={scopeChanges} onChange={(e) => setScopeChanges(e.target.value)} placeholder="What is being added or changed, in the client's words where possible" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="co-notes">Internal notes</Label>
            <Textarea id="co-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {showPrice && (
            <div className="space-y-3 rounded-md border p-3" data-testid="co-pricing">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="co-price">Price ($)</Label>
                  <Input id="co-price" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value === '' ? '' : Number(e.target.value))} placeholder="Fixed price of this change" />
                  <p className="text-xs text-muted-foreground mt-1">What this change adds to the fixed bid, before markup. Leave it empty for no price.</p>
                </div>
                {fixedBid && !useSowMarkup && (
                  <MarkupPercentField id="co-markup" label="This change order's markup (%)" value={ownMarkup} onChange={setOwnMarkup} hint={ownMarkupCheck.ok ? undefined : ownMarkupCheck.error} />
                )}
              </div>
              {fixedBid && (
                <div className="flex items-center gap-2">
                  <Checkbox id="co-use-sow-markup" checked={useSowMarkup} onCheckedChange={(v) => setUseSowMarkup(v === true)} />
                  <label htmlFor="co-use-sow-markup" className="cursor-pointer">Use the SOW's markup ({sowMarkup}%)</label>
                </div>
              )}
              {price !== '' && <FixedBidQuoteLines quote={quote} testId="co-fixed-bid-quote" />}
            </div>
          )}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Suggested config items</Label>
              <Button type="button" size="sm" variant="outline" onClick={suggest} disabled={!valid}>
                <Sparkles className="size-3.5 mr-1" />
                Suggest from text
              </Button>
            </div>
            {suggestions.length === 0 ? (
              <p className="text-xs text-muted-foreground">None yet. Accepted suggestions become config items when the change order is applied.</p>
            ) : (
              <ul className="space-y-1">
                {suggestions.map((s) => (
                  <li key={s.id} className="flex items-start gap-2 text-sm">
                    <Checkbox id={`new-sug-${s.id}`} checked={s.accepted} onCheckedChange={(v) => setSuggestions(suggestions.map((x) => (x.id === s.id ? { ...x, accepted: v === true } : x)))} className="mt-0.5" />
                    <label htmlFor={`new-sug-${s.id}`} className="cursor-pointer">
                      <span className="font-medium">{s.name}</span> <span className="text-muted-foreground">· {s.category}</span>
                      {s.reasoning && <span className="block text-xs text-muted-foreground">{s.reasoning}</span>}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button disabled={!valid || !priceValid || busy} onClick={() => onSubmit({ description: description.trim(), scopeChanges: scopeChanges.trim(), notes: notes.trim() || undefined, suggestedConfigItems: suggestions, ...(pricing !== undefined ? { pricing } : {}) })} data-testid="co-form-submit">
            {existing ? 'Save changes' : 'Create draft'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Transition dialog (one component, fields depend on the target status)
// ---------------------------------------------------------------------------

function TransitionDialog({ co, to, anotherPending, busy, companyId, engagementId, accessToken, loadApplyPlan, onClose, onSubmit }: {
  co: ChangeOrderRecord;
  to: CoStatus;
  anotherPending: boolean;
  busy: boolean;
  companyId: string | null;
  engagementId: string;
  accessToken: string;
  loadApplyPlan: (acceptIds?: string[]) => Promise<ApplyPlan | null>;
  onClose: () => void;
  onSubmit: (request: TransitionRequest) => Promise<void>;
}) {
  // Spec §4.10: applying a CO can bring leftover discovery cells back into scope
  // (cells parked for a change order, or still unfinished on this engagement).
  const [leftovers, setLeftovers] = useState<LeftoverBlock[]>([]);
  const [carry, setCarry] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (to !== 'applied' || !companyId) return;
    let cancelled = false;
    listLeftovers(companyId, engagementId, accessToken)
      .then((r) => {
        if (cancelled) return;
        const parked = r.blocks.map((b) => ({ ...b, cells: b.cells.filter((c) => c.descoped?.to === 'change_order') })).filter((b) => b.cells.length > 0);
        setLeftovers(parked);
        setCarry(new Set(parked.flatMap((b) => b.cells.map((c) => c.cellId))));
      })
      .catch(() => { /* optional section */ });
    return () => { cancelled = true; };
  }, [to, companyId, engagementId, accessToken]);
  const [note, setNote] = useState('');
  const [approverName, setApproverName] = useState('');
  const [method, setMethod] = useState<CoApprovalMethod>('signed_document');
  const [approvedOn, setApprovedOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [billingMethod, setBillingMethod] = useState<'manual' | 'external'>('manual');
  const [invoiceRef, setInvoiceRef] = useState('');
  // Billing a priced change order starts from its total.
  const [amount, setAmount] = useState(co.pricing ? String(co.pricing.total) : '');
  const [currency, setCurrency] = useState('USD');
  const [invoicedAt, setInvoicedAt] = useState(new Date().toISOString().slice(0, 10));
  const [accept, setAccept] = useState<Set<string>>(new Set((co.suggestedConfigItems ?? []).filter((s) => s.accepted).map((s) => s.id)));
  const [plan, setPlan] = useState<ApplyPlan | null>(null);

  useEffect(() => {
    if (to === 'applied') loadApplyPlan().then((p) => p && setPlan(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);

  const title = `${CO_TRANSITION_LABELS[to]}: ${co.changeOrderId}`;

  const valid = useMemo(() => {
    switch (to) {
      case 'approved':
        return approverName.trim().length > 0 && !!approvedOn;
      case 'rejected':
        return reason.trim().length > 0;
      case 'billed':
        return !!invoicedAt && (invoiceRef.trim().length > 0 || Number(amount) > 0);
      default:
        return true;
    }
  }, [to, approverName, approvedOn, reason, invoicedAt, invoiceRef, amount]);

  const submit = () => {
    const req: TransitionRequest = { to, note: note.trim() || undefined };
    if (to === 'pending_approval' && anotherPending) req.force = true;
    if (to === 'approved') req.approval = { approverName: approverName.trim(), method, approvedOn, reference: reference.trim() || undefined };
    if (to === 'rejected') req.rejection = { reason: reason.trim() };
    if (to === 'billed') req.billing = { method: billingMethod, invoiceRef: invoiceRef.trim() || undefined, amount: Number(amount) > 0 ? Number(amount) : undefined, currency, invoicedAt };
    if (to === 'applied') req.apply = { acceptSuggestionIds: [...accept], carryCellIds: [...carry] };
    return onSubmit(req);
  };

  const allSuggestions = co.suggestedConfigItems ?? [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {to === 'pending_approval' && 'Mark this change order as sent to the client. The client signs or confirms offline; you record the outcome here.'}
            {to === 'approved' && "Record the client's acceptance. The scope is not changed until you apply the change order."}
            {to === 'applied' && 'Merge the accepted scope into the engagement: one config item per accepted suggestion, and linked scope deltas are marked resolved.'}
            {to === 'billed' && 'Record the invoice for this change order. PathKit does not bill clients; this is a finance checkpoint.'}
            {to === 'rejected' && "Record that the client declined. You can return the change order to draft to rework it."}
            {to === 'cancelled' && 'Withdraw this change order. This cannot be undone.'}
            {to === 'draft' && 'Return this change order to draft for rework.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {to === 'pending_approval' && anotherPending && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <AlertTriangle className="size-3.5 mt-0.5 shrink-0" aria-hidden="true" />
              Another change order on this engagement is already awaiting approval. Sending a second package can confuse the client; continue only if that is intended.
            </div>
          )}

          {to === 'approved' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="co-approver">Approved by *</Label>
                  <Input id="co-approver" value={approverName} onChange={(e) => setApproverName(e.target.value)} placeholder="Client signatory" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="co-approved-on">Approval date *</Label>
                  <Input id="co-approved-on" type="date" value={approvedOn} onChange={(e) => setApprovedOn(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Method *</Label>
                  <Select value={method} onValueChange={(v) => setMethod(v as CoApprovalMethod)}>
                    <SelectTrigger aria-label="Approval method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {APPROVAL_METHODS.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="co-reference">Reference</Label>
                  <Input id="co-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="DocuSign envelope, email subject…" />
                </div>
              </div>
            </>
          )}

          {to === 'rejected' && (
            <div className="space-y-1.5">
              <Label htmlFor="co-reason">Reason *</Label>
              <Textarea id="co-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          )}

          {to === 'billed' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Method</Label>
                  <Select value={billingMethod} onValueChange={(v) => setBillingMethod(v as 'manual' | 'external')}>
                    <SelectTrigger aria-label="Billing method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual invoice</SelectItem>
                      <SelectItem value="external">External billing system</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="co-invoiced-at">Invoice date *</Label>
                  <Input id="co-invoiced-at" type="date" value={invoicedAt} onChange={(e) => setInvoicedAt(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1.5 sm:col-span-1">
                  <Label htmlFor="co-invoice-ref">Invoice reference</Label>
                  <Input id="co-invoice-ref" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} placeholder="INV-1042" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="co-amount">Amount</Label>
                  <Input id="co-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="co-currency">Currency</Label>
                  <Input id="co-currency" value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">A reference or an amount is required.</p>
            </>
          )}

          {to === 'applied' && (
            <div className="space-y-2">
              <Label>Config items to create</Label>
              {allSuggestions.length === 0 ? (
                <p className="text-sm text-muted-foreground">This change order has no suggested config items. Applying will only mark linked scope deltas as resolved.</p>
              ) : (
                <ul className="space-y-1 border rounded-md p-3">
                  {allSuggestions.map((s) => (
                    <li key={s.id} className="flex items-start gap-2 text-sm">
                      <Checkbox
                        id={`apply-${s.id}`}
                        checked={accept.has(s.id)}
                        onCheckedChange={(v) => setAccept((prev) => { const n = new Set(prev); v === true ? n.add(s.id) : n.delete(s.id); return n; })}
                        className="mt-0.5"
                      />
                      <label htmlFor={`apply-${s.id}`} className="cursor-pointer">
                        <span className="font-medium">{s.name}</span> <span className="text-muted-foreground">· {s.category}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              {leftovers.length > 0 && (
                <div className="space-y-1 mt-3">
                  <Label>Discovery cells parked for a change order</Label>
                  <ul className="space-y-1 border rounded-md p-3">
                    {leftovers.map((b) => (
                      <li key={b.blockId} className="text-sm">
                        <div className="font-medium">{b.name} <span className="text-muted-foreground">· {b.cells.length} cell(s)</span></div>
                        <ul className="pl-1 mt-1 space-y-1">
                          {b.cells.map((c) => (
                            <li key={c.cellId} className="flex items-start gap-2 text-xs">
                              <Checkbox
                                id={`carry-${c.cellId}`}
                                checked={carry.has(c.cellId)}
                                onCheckedChange={(v) => setCarry((prev) => { const n = new Set(prev); v === true ? n.add(c.cellId) : n.delete(c.cellId); return n; })}
                                className="mt-0.5"
                              />
                              <label htmlFor={`carry-${c.cellId}`} className="cursor-pointer">{c.text}{c.descoped?.ref ? <span className="text-muted-foreground"> · parked for {c.descoped.ref}</span> : null}</label>
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">Checked cells come back into this engagement's scope, tagged as restored by {co.changeOrderId}, with their answers kept.</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {accept.size} config item(s) will be created on this engagement
                {plan ? `; ${plan.linkedScopeDeltas.length} linked scope delta(s) will be marked resolved` : ''}.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="co-note">{to === 'cancelled' ? 'Reason' : 'Note'} (optional)</Label>
            <Input id="co-note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={to === 'cancelled' || to === 'rejected' ? 'destructive' : 'default'} disabled={!valid || busy} onClick={submit} data-testid="co-transition-submit">
            {CO_TRANSITION_LABELS[to]}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
